import { useCallback, useMemo, type FC } from "react";
import { Modal, TagsInput, Group, Button } from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../../trpc.js";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMapkaNotification } from "../../../../hooks/notifications.js";
import z from "zod";

const initialValues = {
  invitees: [],
};

interface InviteMembersModalProps {
  opened: boolean;
  close: () => void;
  onError: (errorMessage: string) => void;
}

export const InviteMembersModal: FC<InviteMembersModalProps> = ({ opened, close, onError }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const { mutate: inviteUsers, isPending } = useMutation(trpc.inviteUsers.mutationOptions());
  const mapkaNotification = useMapkaNotification();

  const validate = useMemo(() => zod4Resolver(
    z
      .object({
        invitees: z
          .array(z.email())
          .min(1, intl.formatMessage({
            defaultMessage: "At least one email is required",
            id: "error.atLeastOneEmail",
          })),
      }),
  ), [intl]);

  const { onSubmit, getInputProps, reset } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const handleSubmit = useCallback((form: typeof initialValues) => {
    inviteUsers(form, {
      onSuccess: () => {
        reset();
        close();
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Invitation sent",
            id: "notification.invitationSent",
          }),
          message: intl.formatMessage({
            defaultMessage: "Successfully invited new members",
            id: "notification.invitationSentMessage",
          }),
          variant: "success",
        });
      },
      onError: (error) => {
        onError(error.message);
      },
    });
  }, [inviteUsers, reset, intl, close]);

  const handleClose = useCallback(() => {
    reset();
    close();
  }, [reset, close]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={intl.formatMessage({
        defaultMessage: "Invite members",
        id: "title.inviteMembers",
      })}
    >
      <form onSubmit={onSubmit(handleSubmit)}>
        <TagsInput
          data-autofocus
          disabled={isPending}
          label={intl.formatMessage({
            defaultMessage: "Email addresses",
            id: "label.emailAddresses",
          })}
          {...getInputProps("invitees")}
        />

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="reset" loading={isPending} onClick={handleClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="tertiary" type="submit" loading={isPending}>
            <FormattedMessage defaultMessage="Invite" id="button.invite" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
